/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-prototype-builtins */
/**
 * Function is used to return a copy of the object that is composed of the picked object properties.
 *
 * @since 0.1.0
 * @param object - source object
 * @param keys - properties to pick
 * @returns New object with the picked properties
 * @example
 * ```ts
 * pick({ one: 1, two: 2 }, ["one"]); //=> { one: 1 }
 *
 * pick({ z: "z", x: "x", c: "c" }, ["x", "c"]); //=> { x: "x", c: "c" }
 * ```
 */
export function pick(object, keys) {
    return keys.reduce((obj, key) => {
        if (object && object.hasOwnProperty(key)) {
            obj[key] = object[key];
        }
        return obj;
    }, {});
}
/**
 * Function is used to return an object that contains properties of the given object that satisfy the predicate.
 *
 * @since 0.1.0
 * @param object - source object
 * @param predicate - function to select parameters
 * @returns New object with matching parameter
 * @example
 * ```ts
 * pickBy({ one: 1, two: 2 }, (value) => value > 1); //=> { two: 2 }
 * ```
 */
export function pickBy(object, predicate) {
    const obj = {};
    for (const key in object) {
        if (predicate(object[key])) {
            obj[key] = object[key];
        }
    }
    return obj;
}
/**
 * Function is used to return an object that contains properties of the given object that satisfy the predicate.
 *
 * @since 0.1.0
 * @param collection - source object
 * @param iteratee - function to select parameters
 * @returns New object with matching parameter
 */
export function groupBy(collection, iteratee) {
    return collection.reduce((r, v, _i, _a, k = iteratee(v)) => {
        (r[k] || (r[k] = [])).push(v);
        return r;
    }, {});
}
/**
 * Function is used to return a copy of the object that composed of the own properties of the given object that are not omitted.
 * It is the opposite of the {@link pick} function.
 *
 * @since 0.1.0
 * @param object - source object
 * @param keys - properties to omit
 * @returns New object without omitted properties
 * @example
 * ```ts
 * omit({ one: 1, two: 2 }, ["one"]); //=> { two: 2 }
 *
 * omit({ z: "z", x: "x", c: "c" }, ["x", "c"]); //=> { z: "z" }
 * ```
 * @see {@link pick}
 */
export function omit(object, keys) {
    object = { ...object };
    keys.forEach((prop) => delete object[prop]);
    return object;
}
/**
 * Checks if `value` is not an empty object, collection, map, or set.
 *
 * @since 0.1.0
 * @param value - value to check
 * @returns Returns `true` if `value` is not empty, else `false`
 * @example
 * ```ts
 * notEmpty({ one: 1, two: 2 }); //=> true
 *
 * notEmpty(null); //=> false
 *
 * notEmpty(undefined); //=> false
 * ```
 */
export function notEmpty(value) {
    if (value === null || value === undefined)
        return false;
    return true;
}
/**
 * Normalizes an array of objects by the specified key value.
 * For quick access to the object by the specified key.
 *
 * @since 0.1.0
 * @param items - Array of objects
 * @param key - Normalization key (is optional, by default is `id`)
 * @returns Normalized data structure and normalization order
 * @example
 * ```ts
 * const items = [
 *   { id: "1", name: "Mark" },
 *   { id: "3", name: "Nathan" },
 *   { id: "2", name: "Alan" },
 * ];
 *
 * normalize(items, "id");
 * // => {
 * //   byId: {
 * //     1: { id: "1", name: "Mark" },
 * //     3: { id: "3", name: "Nathan" },
 * //     2: { id: "2", name: "Alan" },
 * //   },
 * //   order: ["1", "3", "2"],
 * // }
 * ```
 */
export function normalize(...args) {
    const items = args[0];
    const idSelector = args[1];
    return items.reduce((acc, item) => {
        const key = (idSelector
            ? typeof idSelector === "function"
                ? idSelector(item)
                : item[idSelector]
            : item["id"]);
        acc.byId[key] = item;
        acc.order.push(String(key));
        return acc;
    }, { byId: {}, order: [] });
}
/**
 * Creates an object with the same keys as `object` and values generated
 * by running each own enumerable string keyed property of `object` thru
 * `iteratee`. The iteratee is invoked with three arguments:
 * (value, key, object).
 *
 * @since 1.2.1
 * @param object - source object
 * @param iteratee - invoked per iteration on the object
 * @returns New mapped object
 * @example
 * ```ts
 * const users = {
 *   fred: { user: "fred", age: 40 },
 *   pebbles: { user: "pebbles", age: 1 },
 * };
 *
 * mapValues(users, ({ age }) => age); //=> { fred: 40, pebbles: 1 }
 * ```
 */
export function mapValues(object, iteratee) {
    return Object.entries(object).reduce((acc, [key, value]) => ({
        ...acc,
        [key]: iteratee(value, key, object),
    }), {});
}
/**
 * Returns a new array by toggling the specified element.
 * If an element is present, the result will be an array without that element.
 * If an element was missing, this element will be added to the result array.
 *
 * @since 1.3.28
 * @param list - source array
 * @param item - item for toggling
 * @returns New array with toggled item
 * @example
 * ```ts
 * const list = ["a", "b", "c"];
 *
 * toggle(list, "c"); //=> ["a", "b"]
 * ```
 * @example
 * ```ts
 * const list = ["a", "b"];
 *
 * toggle(list, "c"); //=> ["a", "b", "c"]
 * ```
 */
export function toggle(list, item) {
    return list.includes(item) ? list.filter((el) => el !== item) : [...list, item];
}
/**
 * Returns a new array by toggling the specified element.
 * If an element is present, the result will be an array without that element.
 * If an element was missing, this element will be added to the result array.
 *
 * @since 1.3.28
 * @param list - source array
 * @param item - item for toggling
 * @param iteratee - the iteratee invoked per element
 * @returns New array with toggled item
 * @example
 * ```ts
 * const list = [{asin: "a"}, {asin: "b"}, {asin: "c"}];
 *
 * toggleBy(list, {asin: "c"}, "asin"); //=> [{asin: "a"}, {asin: "b"}]
 * ```
 * @example
 * ```ts
 * const list = [{asin: "a"}, {asin: "b"}];
 *
 * toggleBy(list, {asin: "c"}, "asin"); //=> [{asin: "a"}, {asin: "b"}, {asin: "c"}]
 * ```
 */
export function toggleBy(list, item, iteratee) {
    return list.some((el) => el[iteratee] === item[iteratee])
        ? list.filter((el) => el[iteratee] !== item[iteratee])
        : [...list, item];
}
export function roundNumber(value, fractionDigits = 0) {
    const pow = 10 ** fractionDigits;
    return Math.round(value * pow) / pow;
}
export function capitalize(s) {
    if (typeof s !== "string")
        return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
}
export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
export function getRandomInt(max) {
    return Math.floor(Math.random() * Math.floor(max));
}
export function htmlDecode(input) {
    const doc = new DOMParser().parseFromString(input, "text/html");
    return doc.documentElement.textContent;
}
export function isRobot() {
    const botPattern = new RegExp(/bot|crawler|spider|crawling/i, "i");
    return botPattern.test(navigator.userAgent);
}
